const MOBILE_MAX_WIDTH = 767;

/**
 * True when the current viewport is mobile-sized (< 768px), matching the
 * `useIsMobile` hook / Tailwind `md` breakpoint. Evaluated at call time
 * (click handlers don't need reactivity).
 */
export function isMobileViewport() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`).matches;
}

/**
 * Deliver a generated PDF blob to the user, consistently across the site:
 *   - Desktop: open the PDF in a new tab for preview.
 *   - Mobile: trigger a direct download. Mobile browsers handle blob-URL
 *     previews poorly (popup blocking, no inline PDF viewer), so a download
 *     is the reliable behaviour there.
 *
 * @param {Blob} blob - the PDF blob to deliver.
 * @param {Response} [response] - the originating fetch Response; its
 *   Content-Disposition header (when present) provides the download filename.
 * @param {string} [fallbackFilename='document.pdf'] - filename used for the
 *   mobile download when the response has no Content-Disposition.
 */
export function deliverPdfBlob(blob, response, fallbackFilename = 'document.pdf') {
  const url = window.URL.createObjectURL(blob);

  if (isMobileViewport()) {
    let filename = fallbackFilename;
    const disposition =
      response && response.headers && response.headers.get('Content-Disposition');
    const match =
      disposition && disposition.match(/filename\*?=(?:UTF-8'')?"?([^";\n]+)"?/i);
    if (match) {
      try {
        filename = decodeURIComponent(match[1]);
      } catch {
        filename = match[1];
      }
    }
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } else {
    // Preview in a new tab. The blob URL is intentionally not revoked here so
    // the opened tab keeps access to it.
    window.open(url, '_blank');
  }
}

export default deliverPdfBlob;
